import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const CurrencyContext = createContext();

export const currencies = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'ILS', symbol: '₪', name: 'Israeli Shekel' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc' }
];

export function CurrencyProvider({ children }) {
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    return localStorage.getItem('preferredCurrency') || 'USD';
  });
  const [rates, setRates] = useState(() => {
    const savedRates = localStorage.getItem('currencyRates');
    const savedTimestamp = localStorage.getItem('currencyRatesTimestamp');
    // If we have saved rates and they're less than 1 hour old, use them
    if (savedRates && savedTimestamp && (Date.now() - parseInt(savedTimestamp)) < 3600000) {
      return JSON.parse(savedRates);
    }
    return null;
  });
  const [loading, setLoading] = useState(!rates);

  useEffect(() => {
    localStorage.setItem('preferredCurrency', selectedCurrency);
  }, [selectedCurrency]);

  useEffect(() => {
    const fetchRates = async () => {
      try {
        setLoading(true);
        // Get rates against USD using a more reliable API
        const response = await axios.get('https://open.er-api.com/v6/latest/USD');
        
        if (response.data.result === 'success') {
          // Transform the rates to match our currency codes
          const newRates = {
            usd: 1, // USD is always 1
            eur: response.data.rates.EUR,
            gbp: response.data.rates.GBP,
            jpy: response.data.rates.JPY,
            cny: response.data.rates.CNY,
            ils: response.data.rates.ILS,
            inr: response.data.rates.INR,
            cad: response.data.rates.CAD,
            aud: response.data.rates.AUD,
            chf: response.data.rates.CHF
          };

          setRates(newRates);
          localStorage.setItem('currencyRates', JSON.stringify(newRates));
          localStorage.setItem('currencyRatesTimestamp', Date.now().toString());
        } else {
          throw new Error('Failed to fetch rates');
        }
      } catch (error) {
        console.error('Error fetching currency rates:', error);
        // If we have old rates, keep using them
        if (!rates) {
          // If we don't have any rates, set USD as 1 and others as 0
          const fallbackRates = {
            usd: 1,
            eur: 0,
            gbp: 0,
            jpy: 0,
            cny: 0,
            ils: 0,
            inr: 0,
            cad: 0,
            aud: 0,
            chf: 0
          };
          setRates(fallbackRates);
          localStorage.setItem('currencyRates', JSON.stringify(fallbackRates));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRates();
  }, []);

  const convertValue = (value, fromCurrency = 'USD') => {
    if (!rates) return value;
    
    const fromRate = rates[fromCurrency.toLowerCase()] || 1;
    const toRate = rates[selectedCurrency.toLowerCase()] || 1;
    
    // Convert from USD to the target currency
    return (value * toRate) / fromRate;
  };

  const formatCurrency = (value, fromCurrency = 'USD') => {
    const convertedValue = convertValue(value, fromCurrency);
    const currency = currencies.find(c => c.code === selectedCurrency);
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: selectedCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(convertedValue);
  };

  const value = {
    selectedCurrency,
    setSelectedCurrency,
    currencies,
    rates,
    loading,
    convertValue,
    formatCurrency
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
} 